import {
  type DiscoverChatbotProfileOverlayLabels,
  type DiscoverChatbotProfileOverlayStats as DiscoverChatbotProfileOverlayStatsData,
  formatDiscoverChatbotCardCount,
} from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React from "react";

type StatKey = "messages" | "likes" | "tokens";

type StatItem = {
  readonly key: StatKey;
  readonly icon: IconVariants;
  readonly value: string;
  readonly label: string;
};

const DEFAULT_TEST_ID = "DiscoverChatbotProfileOverlayStats";

type DiscoverChatbotProfileOverlayStatsProps = {
  readonly stats: DiscoverChatbotProfileOverlayStatsData;
  readonly labels: Pick<DiscoverChatbotProfileOverlayLabels, StatKey>;
  readonly testID?: string;
};

const WRAPPER_CLASSNAME = "flex gap-5";
const STAT_ITEM_CLASSNAME = "flex flex-col";
const STAT_ITEM_HEADER_CONTAINER_CLASSNAME = "flex items-center gap-0.5";
const STAT_LABEL_CLASSNAME = "text-gray-11";

const buildStatItems = (stats: DiscoverChatbotProfileOverlayStatsData, labels: DiscoverChatbotProfileOverlayStatsProps["labels"]): StatItem[] => {
  const statItems: StatItem[] = [];

  if (stats.messageCount !== undefined) {
    statItems.push({
      key: "messages",
      icon: IconVariants.MESSAGE,
      value: formatDiscoverChatbotCardCount(stats.messageCount),
      label: labels.messages,
    });
  }

  if (stats.ratingPercent !== undefined) {
    statItems.push({ key: "likes", icon: IconVariants.LIKE, value: `${stats.ratingPercent}%`, label: labels.likes });
  }

  if (stats.tokenCount !== undefined) {
    statItems.push({ key: "tokens", icon: IconVariants.BLOCKS, value: formatDiscoverChatbotCardCount(stats.tokenCount), label: labels.tokens });
  }

  return statItems;
};

const DiscoverChatbotProfileOverlayStats = ({ stats, labels, testID }: DiscoverChatbotProfileOverlayStatsProps) => {
  const statItems = buildStatItems(stats, labels);

  if (statItems.length === 0) {
    return null;
  }

  const wrapperTestId = testID ? `${testID}-stats` : `${DEFAULT_TEST_ID}`;

  return (
    <div className={WRAPPER_CLASSNAME} data-testid={wrapperTestId}>
      {statItems.map((statItem) => (
        <div className={STAT_ITEM_CLASSNAME} key={statItem.key}>
          <div className={STAT_ITEM_HEADER_CONTAINER_CLASSNAME}>
            <Icon icon={statItem.icon} size={IconSize.SMALL} />
            <Text as="span" variant={TextVariants.LABEL_LARGE_BOLD}>
              {statItem.value}
            </Text>
          </div>
          <Text as="span" variant={TextVariants.LABEL_SMALL_MEDIUM} className={STAT_LABEL_CLASSNAME}>
            {statItem.label}
          </Text>
        </div>
      ))}
    </div>
  );
};

export default React.memo(DiscoverChatbotProfileOverlayStats);
